// No React import needed with modern JSX transform
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/atoms/button";
import { Card } from "@/components/ui/molecules/card";
import { ArrowRight } from "lucide-react";

interface DemoPageLink {
  title: string;
  description: string;
  path: string;
}

const ComponentDemo = () => {
  const demoPages: DemoPageLink[] = [
    {
      title: "Button Test",
      description:
        "Showcases various button styles, states, and button group components",
      path: "/button-test",
    },
    {
      title: "Navigation Button Test",
      description:
        "Demonstrates navigation button variants including default, error, and disabled states",
      path: "/navigation-button-test",
    },
    {
      title: "Input Test",
      description:
        "Displays various input field variants with different states, sizes, and features",
      path: "/input-test",
    },
    {
      title: "Avatar Test",
      description:
        "Demonstrates avatar components with different sizes, initials, images, and edit functionality",
      path: "/avatar-test",
    },
    {
      title: "Checkbox Test",
      description:
        "Shows checkbox components with different states including checked, unchecked, indeterminate, and disabled",
      path: "/checkbox-test",
    },
    {
      title: "Radio Test",
      description:
        "Displays radio button components with different states including checked, unchecked, and disabled variants",
      path: "/radio-test",
    },
    {
      title: "Selection Card Test",
      description:
        "Demonstrates selection card components with different states and variations",
      path: "/selection-card-test",
    },
    {
      title: "Button Card Test",
      description:
        "Showcases MButtonCard variants including count default and plain small",
      path: "/mbutton-card-test",
    },
    {
      title: "Progress Test",
      description:
        "Shows progress bar components with different sizes, states, and interactive functionality",
      path: "/progress-test",
    },
    {
      title: "Toggle Test",
      description:
        "Displays toggle switch components with different states, sizes, and usage examples",
      path: "/toggle-test",
    },
    {
      title: "Dialog Test",
      description:
        "Demonstrates modal dialog components with different action button configurations and states",
      path: "/dialog-test",
    },
    {
      title: "Modal Dialog Test",
      description:
        "Showcases a custom modal dialog built from scratch with action buttons and various states",
      path: "/modal-dialog-test",
    },
    {
      title: "Listing Demo",
      description:
        "Shows data table implementation with filtering and pagination",
      path: "/ListingDemo",
    },
    {
      title: "List Item Demo",
      description:
        "Demonstrates list item components with different usage types, states, and upload progress",
      path: "/list-item-demo",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F6F6F6] p-8">
      <div className="mx-auto max-w-4xl space-y-8 bg-semantic-background-surface-lvl-2 p-8 rounded-lg shadow-sm">
        <h1 className="text-2xl font-bold text-primary-indigo-800 mb-6">
          UI Component Demos
        </h1>
        <p className="text-neutral-600 mb-8">
          Browse through our collection of UI component demos to see various
          components in action.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {demoPages.map((page) => (
            <Card key={page.path} className="overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-primary-indigo-600 mb-2">
                  {page.title}
                </h2>
                <p className="text-neutral-600 mb-4">{page.description}</p>
                <Link to={page.path}>
                  <Button
                    variant="outline"
                    className="w-full flex justify-between items-center"
                  >
                    <span>View Demo</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComponentDemo;
